import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { RequirePermissions } from "@/app/router/guards";
import { PermissionCodes } from "@/shared/auth/permissions";
import { LazyPage } from "@/app/router/LazyPage";

const BomsListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.BomsListPage })),
);
const BomDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.BomDetailPage })),
);
const ProductsListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.ProductsListPage })),
);
const ProductTypesListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.ProductTypesListPage })),
);
const ProductTypeDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.ProductTypeDetailPage })),
);
const TechnicalInfosListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.TechnicalInfosListPage })),
);
const TechnicalInfoDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.TechnicalInfoDetailPage })),
);
const ProductDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.ProductDetailPage })),
);
const RoutingsListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.RoutingsListPage })),
);
const RoutingCreatePage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.RoutingCreatePage })),
);
const RoutingDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.RoutingDetailPage })),
);
const SpecsListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.SpecsListPage })),
);
const SpecDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.SpecDetailPage })),
);
const QcPlansListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.QcPlansListPage })),
);
const QcPlanDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({ default: m.QcPlanDetailPage })),
);
const OperationTemplatesListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({
    default: m.OperationTemplatesListPage,
  })),
);
const OperationTemplateDetailPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({
    default: m.OperationTemplateDetailPage,
  })),
);
const PackFormatsListPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({
    default: m.PackFormatsListPage,
  })),
);
const EngOnboardingHubPage = lazy(() =>
  import("@/modules/engineering").then((m) => ({
    default: m.EngOnboardingHubPage,
  })),
);
const EngReleaseBoardPage = lazy(() =>
  import("@/modules/approvals").then((m) => ({ default: m.EngReleaseBoardPage })),
);

const productTypeAccess = [
  PermissionCodes.EngProductTypeList,
  PermissionCodes.EngProductTypeManage,
];

const productAccess = [
  PermissionCodes.EngProductList,
  PermissionCodes.EngProductManage,
  PermissionCodes.EngBomApprove,
];

const packFormatAccess = [
  PermissionCodes.EngPackFormatList,
  PermissionCodes.EngPackFormatManage,
  PermissionCodes.EngBomApprove,
];

export const engineeringRoutes: RouteObject[] = [
          {
            element: (
              <RequirePermissions codes={productTypeAccess} match="any" />
            ),
            children: [
              {
                path: "eng/product-types",
                element: (
                  <LazyPage>
                    <ProductTypesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/product-types/:productTypeCode/edit",
                element: (
                  <LazyPage>
                    <ProductTypeDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/product-types/:productTypeCode",
                element: (
                  <LazyPage>
                    <ProductTypeDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions codes={productAccess} match="any" />
            ),
            children: [
              {
                path: "eng/products",
                element: (
                  <LazyPage>
                    <ProductsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/products/new",
                element: (
                  <LazyPage>
                    <ProductsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/products/:productCode/edit",
                element: (
                  <LazyPage>
                    <ProductDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/products/:productCode",
                element: (
                  <LazyPage>
                    <ProductDetailPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions codes={packFormatAccess} match="any" />
            ),
            children: [
              {
                path: "eng/pack-formats",
                element: (
                  <LazyPage>
                    <PackFormatsListPage />
                  </LazyPage>
                ),
              },
            ],
          },
          {
            element: (
              <RequirePermissions
                codes={[
                  PermissionCodes.EngBomList, PermissionCodes.EngBomManage, PermissionCodes.EngBomApprove,
                  PermissionCodes.EngTechnicalInfoList, PermissionCodes.EngTechnicalInfoManage, PermissionCodes.EngTechnicalInfoApprove,
                  PermissionCodes.EngRoutingList, PermissionCodes.EngRoutingManage, PermissionCodes.EngRoutingApprove,
                  PermissionCodes.EngOperationTemplateList, PermissionCodes.EngOperationTemplateManage,
                  PermissionCodes.EngSpecList, PermissionCodes.EngSpecManage, PermissionCodes.EngSpecApprove,
                  PermissionCodes.EngQcPlanList, PermissionCodes.EngQcPlanManage, PermissionCodes.EngQcPlanApprove,
                  PermissionCodes.ApprovalAct,
                ]}
                match="any"
              />
            ),
            children: [
              {
                path: "eng/technical-infos",
                element: (
                  <LazyPage>
                    <TechnicalInfosListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/technical-infos/:code",
                element: (
                  <LazyPage>
                    <TechnicalInfoDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/boms",
                element: (
                  <LazyPage>
                    <BomsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/boms/new",
                element: (
                  <LazyPage>
                    <BomsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/boms/:bomCode",
                element: (
                  <LazyPage>
                    <BomDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/routings",
                element: (
                  <LazyPage>
                    <RoutingsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/routings/new",
                element: (
                  <LazyPage>
                    <RoutingCreatePage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/routings/:routingCode",
                element: (
                  <LazyPage>
                    <RoutingDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/specs",
                element: (
                  <LazyPage>
                    <SpecsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/specs/new",
                element: (
                  <LazyPage>
                    <SpecsListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/specs/:specCode",
                element: (
                  <LazyPage>
                    <SpecDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/qc-plans",
                element: (
                  <LazyPage>
                    <QcPlansListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/qc-plans/new",
                element: (
                  <LazyPage>
                    <QcPlansListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/qc-plans/:planCode",
                element: (
                  <LazyPage>
                    <QcPlanDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/releases",
                element: (
                  <LazyPage>
                    <EngReleaseBoardPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/operation-templates",
                element: (
                  <LazyPage>
                    <OperationTemplatesListPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/operation-templates/:templateCode",
                element: (
                  <LazyPage>
                    <OperationTemplateDetailPage />
                  </LazyPage>
                ),
              },
              {
                path: "eng/onboarding",
                element: (
                  <LazyPage>
                    <EngOnboardingHubPage />
                  </LazyPage>
                ),
              },
            ],
          },
];
